import { Briefcase, Users, CheckCircle2, Clock, ChevronRight, Grid, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

interface Space {
  id: string;
  name: string;
  type: string;
  total: number;
  completed: number;
}

interface ClientSpacesOverviewProps {
  spaces: Space[];
  onViewSpace?: (spaceId: string) => void;
}

export function ClientSpacesOverview({ spaces, onViewSpace }: ClientSpacesOverviewProps) {
  const getSpaceIcon = (type: string) => {
    if (type === 'jsm') return <Briefcase className="size-5 text-blue-600" />;
    if (type === 'jira') return <Grid className="size-5 text-purple-600" />;
    if (type === 'confluence') return <FileText className="size-5 text-green-600" />;
    if (type === 'product-discovery') return <Grid className="size-5 text-amber-600" />;
    return <Briefcase className="size-5 text-gray-600" />;
  };

  // Helper to format space type for display
  const formatSpaceType = (type: string) => {
    const typeMap: Record<string, string> = {
      'jsm': 'JSM',
      'jira': 'Jira',
      'confluence': 'Confluence',
      'product-discovery': 'Product Discovery',
    };
    return typeMap[type] || type;
  };

  if (spaces.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Briefcase className="size-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">No spaces configured</p>
          <p className="text-sm">Configure Jira Service Spaces to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Jira Service Spaces</CardTitle>
        <CardDescription>Configuration items organized by service space</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map(space => {
            const progressPercentage = space.total > 0 
              ? Math.round((space.completed / space.total) * 100) 
              : 0;
            
            return (
              <div
                key={space.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => onViewSpace?.(space.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getSpaceIcon(space.type)}
                    <div>
                      <h4 className="text-base font-medium">{space.name}</h4>
                      <p className="text-xs text-muted-foreground capitalize">
                        {formatSpaceType(space.type)}
                      </p>
                    </div>
                  </div>
                  {onViewSpace && (
                    <Button variant="ghost" size="sm">
                      <ChevronRight className="size-4" />
                    </Button>
                  )}
                </div>

                <div className="mb-2">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{progressPercentage}%</span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="size-3" />
                    <span>{space.completed} completed</span>
                  </div>
                  <div className="text-muted-foreground">
                    {space.total} total
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}